import type { State } from '@progress/kendo-data-query';

/**
 * US 35406: Ask Ryn changes the live grid on the current page. A Ryn reply carries
 * `{ kind: 'gridCommands', document: GridCommandsDocument }`. The command shape is
 * Kendo's own GridAICommand vocabulary (Kendo Angular Grid 22 AI Assistant), so the
 * seat speaks Kendo descriptors; our applier, not Kendo's hidden processor, runs it.
 * Design: documentation/solution-briefs/35406-ask-ryn-grid-state.md.
 */
export const GRID_COMMAND_TYPES = [
  'GridSort',
  'GridClearSort',
  'GridFilter',
  'GridClearFilter',
  'GridGroup',
  'GridClearGroup',
  'GridColumnShow',
  'GridColumnHide',
] as const;

export type GridCommandType = (typeof GRID_COMMAND_TYPES)[number];

export type GridFieldType = 'text' | 'numeric' | 'date' | 'boolean';

/** One column the page offers to Ask Ryn. Only these fields may appear in a command. */
export interface GridCatalogColumn {
  readonly field: string;
  readonly title: string;
  readonly type: GridFieldType;
  readonly hidden: boolean;
  /** What the grid allows on this column (Kendo `groupable` / `sortable` / `filterable`). */
  readonly groupable: boolean;
  readonly sortable: boolean;
  readonly filterable: boolean;
}

/** What a registered grid tells Ask Ryn about itself. Never row data. */
export interface GridCatalog {
  readonly gridKey: string;
  readonly title: string;
  readonly columns: readonly GridCatalogColumn[];
}

/** The part of a grid's view that commands may change. */
export interface GridView {
  readonly state: State;
  readonly hidden: readonly string[];
}

export interface GridFilterLeaf {
  field: string;
  operator: string;
  value?: string | number | boolean | Date | null;
  ignoreCase?: boolean;
}

export interface GridFilterGroup {
  logic: 'and' | 'or';
  filters: (GridFilterLeaf | GridFilterGroup)[];
}

/** A validated command, values already converted to the column's type. */
export interface GridCommand {
  type: GridCommandType;
  sort?: { field: string; dir: 'asc' | 'desc' };
  filter?: GridFilterGroup;
  group?: { field: string; dir?: 'asc' | 'desc' };
  id?: string;
}

export interface GridCommandsDocument {
  version: 1;
  gridKey: string;
  commands: GridCommand[];
}

export type GridCommandsParse =
  | { ok: true; document: GridCommandsDocument; error?: undefined }
  | { ok: false; error: string; document?: undefined };

/** A grid page registers this while mounted (AskRynGridRegistry). */
export interface AskRynGridAdapter {
  readonly gridKey: string;
  catalog(): GridCatalog;
  view(): GridView;
  /** Put this view on the grid through its own state API (and column visibility). */
  // eslint-disable-next-line no-unused-vars -- pre-commit-allow: base no-unused-vars misreads an interface method parameter (US 35406)
  apply(next: GridView): void;
}

/** The last Ask Ryn change, for the page's "changed by Ask Ryn, Undo" chip. */
export interface AskRynGridChange {
  readonly gridKey: string;
  readonly summary: string;
}
